import { isFreeFlow } from '@forwardflow/features/campaign-v2/utils/demo-mode'
import { campaignFlowMock } from '@forwardflow/features/campaign-flow/services/campaign-flow.mock'
import {
   campaignFlowLive,
   startNewLiveSession,
} from '@forwardflow/features/campaign-flow/services/campaign-flow.live'
import { ICampaignFlowService } from '@forwardflow/features/campaign-flow/services/campaign-flow.service'

/**
 * Demo or live, decided on every call:
 *  - the no-sign-in preview (`/preview/campaign-flow`), the sandbox and the
 *    dev free flow (`?dev=1`) use the in-browser demo, so nothing is written;
 *  - everywhere else (signed in, `/campaign/flow`) uses the real backend
 *    through the campaign v2 adapter.
 * `?flow=demo` forces the demo for a signed-in walkthrough.
 *
 * Signed-in routes use the configured API in local and deployed builds.
 * Demo mode is selected explicitly; it never depends on the build hostname.
 */
const FLOW_MODE_KEY = 'ff-campaign-flow-mode'

export const isDemoFlow = (): boolean => {
   if (typeof window === 'undefined') return true
   if (window.location.pathname.startsWith('/preview/')) return true
   const asked = new URLSearchParams(window.location.search).get('flow')
   /* Remember the choice for this tab, so links inside the flow keep it. */
   try {
      if (asked === 'live' || asked === 'demo')
         sessionStorage.setItem(FLOW_MODE_KEY, asked)
   } catch {
      /* private mode: the URL still decides */
   }
   let mode = asked
   try {
      mode = mode || sessionStorage.getItem(FLOW_MODE_KEY)
   } catch {
      /* ignore */
   }
   if (mode === 'demo') return true
   if (isFreeFlow()) return true
   return false
}

const pick = (): ICampaignFlowService =>
   isDemoFlow() ? campaignFlowMock : campaignFlowLive

export const campaignFlowService = new Proxy({} as ICampaignFlowService, {
   get: (_t, key: keyof ICampaignFlowService) => pick()[key],
})

/** Call when "New campaign" starts a fresh draft. */
export const startNewCampaign = () => {
   if (!isDemoFlow()) startNewLiveSession()
}

export type { ICampaignFlowService }
